import type { User } from "@itaas/core/auth/models/user.model";
import { hasPermission } from "@itaas/core/rbac/access";
import { isTenantAdminRole } from "@itaas/core/admin/module-grants";

export function isPlatformOperator(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  const slugs = user.roles?.map((role) => role.slug) ?? [];
  return slugs.some((slug) => slug === "PLATFORM_ADMIN" || slug === "PLATFORM_OWNER");
}

export function canReadPlatformSchools(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isPlatformOperator(user)) return true;
  return hasPermission(user, "edu.platform.tenants.read");
}

export function canManagePlatformSchools(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isPlatformOperator(user)) return true;
  return hasPermission(user, "edu.platform.tenants.manage");
}

export function canAccessPlatformCommercial(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isPlatformOperator(user)) return true;
  return hasPermission(user, "pep.leads.read");
}

export function canAccessPlatformUsers(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isPlatformOperator(user)) return true;
  const roleSlug = user.roles?.[0]?.slug;
  if (isTenantAdminRole(roleSlug)) return true;
  return (
    hasPermission(user, "user.read") ||
    hasPermission(user, "role.read") ||
    hasPermission(user, "invitation.read")
  );
}

export function canAccessPlatformSettings(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isPlatformOperator(user)) return true;
  return hasPermission(user, "settings.manage") || hasPermission(user, "settings.read");
}

export function canAccessPlatformHub(user: User | null | undefined): boolean {
  return (
    canReadPlatformSchools(user) ||
    canAccessPlatformUsers(user) ||
    canAccessPlatformCommercial(user) ||
    canAccessPlatformSettings(user)
  );
}

export function defaultPlatformPath(user: User | null | undefined): string {
  if (canReadPlatformSchools(user)) return "/platform/schools";
  if (canAccessPlatformUsers(user)) return "/platform/users";
  if (canAccessPlatformCommercial(user)) return "/platform/commercial";
  if (canAccessPlatformSettings(user)) return "/platform/settings";
  return "/modules";
}
